import { useEffect, useRef, useState } from "react";
import { Button, Modal, Tag } from "antd";
import { AlertTriangle, CheckCircle2, FileText, Upload } from "lucide-react";
import dayjs from "dayjs";
import axiosClient from "../../api/axiosClient";
// Shares the drop zone / preview styles with the Clients CSV import.
import "../AdminClientsPage/AdminClientsPage.css";

// ── Types ─────────────────────────────────────────────────────────────────────

type Staff = { user_id: string; name: string; email?: string };
type Service = { service_id: string; name: string; price: number; duration_min: number };

type Status = "scheduled" | "completed" | "cancelled" | "no_show";

type ParsedAppointment = {
  row: number;
  client_name: string;
  client_email: string;
  client_phone: string;
  staff_id: string;
  staff_name: string;
  service: string;
  service_id?: string;
  start: Date;
  end: Date;
  price: number;
  status: Status;
  customService: boolean; // not in the salon's service list
};

type ParseResult = {
  added: ParsedAppointment[];
  skipped: { row: number; reason: string }[];
};

type ImportResponse = {
  inserted: number;
  clients_created: number;
  duplicates: number;
  failed: { row: number; reason: string }[];
};

type ImportStatus = "idle" | "parsing" | "preview" | "importing" | "done" | "error";

// ── CSV parsing ───────────────────────────────────────────────────────────────

// RFC 4180-ish: quoted fields, "" escapes, commas/newlines inside quotes.
// Tab-separated files are detected from the header line.
function splitCSV(text: string): string[][] {
  const firstLine = text.split(/\r?\n/, 1)[0] ?? "";
  const sep = firstLine.includes("\t") && !firstLine.includes(",") ? "\t" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"' && text[i + 1] === '"') { cur += '"'; i++; }
      else if (ch === '"') inQ = false;
      else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === sep) { row.push(cur.trim()); cur = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cur.trim()); rows.push(row); row = []; cur = "";
    } else cur += ch;
  }
  if (cur !== "" || row.length) { row.push(cur.trim()); rows.push(row); }
  return rows.filter((r) => r.some((c) => c !== ""));
}

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

// Column aliases, most specific first. An exact header match wins over a
// partial one, and each column is claimed by at most one field.
const FIELD_ALIASES: Record<string, string[]> = {
  client: ["clientname", "client", "customername", "customer", "guest", "name", "fullname"],
  email: ["clientemail", "customeremail", "email", "emailaddress"],
  phone: ["clientphone", "customerphone", "phone", "mobile", "cell", "phonenumber", "telephone"],
  staff: ["staffname", "staff", "stylist", "barber", "provider", "employee", "teammember", "with"],
  service: ["servicename", "service", "treatment", "appointmenttype"],
  date: ["date", "appointmentdate", "day", "startdate"],
  time: ["starttime", "time", "start", "appointmenttime"],
  duration: ["durationmin", "durationminutes", "duration", "length", "minutes", "mins"],
  price: ["price", "amount", "total", "cost", "charge"],
  status: ["status", "appointmentstatus", "state"],
};

function mapColumns(headers: string[]): Record<string, number> {
  const h = headers.map(norm);
  const taken = new Set<number>();
  const out: Record<string, number> = {};
  for (const pass of ["exact", "partial"] as const) {
    for (const [field, aliases] of Object.entries(FIELD_ALIASES)) {
      if (out[field] !== undefined) continue;
      for (const a of aliases) {
        const idx = h.findIndex((col, i) => !taken.has(i) && (pass === "exact" ? col === a : col.includes(a)));
        if (idx >= 0) { out[field] = idx; taken.add(idx); break; }
      }
    }
  }
  return out;
}

// Dates: 2026-11-05, 11/5/2026, 11/5/26 (US order). May carry a time after it.
function parseDate(s: string): { y: number; m: number; d: number; rest: string } | null {
  let m = s.match(/^\s*(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s]+(.*))?$/);
  if (m) return { y: +m[1], m: +m[2], d: +m[3], rest: m[4] ?? "" };
  m = s.match(/^\s*(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})(?:[\s,]+(.*))?$/);
  if (m) {
    const y = m[3].length === 2 ? 2000 + +m[3] : +m[3];
    return { y, m: +m[1], d: +m[2], rest: m[4] ?? "" };
  }
  return null;
}

// Times: 14:30, 2:30 PM, 2pm, 9:00a, 14:30:00
function parseTime(s: string): { h: number; min: number } | null {
  const m = s.trim().match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([ap])?\.?\s*m?\.?$/i);
  if (!m) return null;
  let h = +m[1];
  const min = m[2] ? +m[2] : 0;
  const ap = m[3]?.toLowerCase();
  if (ap === "p" && h < 12) h += 12;
  if (ap === "a" && h === 12) h = 0;
  if (h > 23 || min > 59) return null;
  return { h, min };
}

function parseStatus(s: string, start: Date): Status | null {
  const v = norm(s);
  if (!v) return start.getTime() < Date.now() ? "completed" : "scheduled";
  if (["completed", "complete", "done", "checkedout", "paid", "finished", "closed"].includes(v)) return "completed";
  if (["cancelled", "canceled", "cancel"].includes(v)) return "cancelled";
  if (["noshow", "missed"].includes(v)) return "no_show";
  if (["scheduled", "booked", "confirmed", "upcoming", "pending", "open"].includes(v)) return "scheduled";
  return null;
}

function parseAppointmentsCSV(text: string, staff: Staff[], services: Service[]): ParseResult {
  const rows = splitCSV(text);
  if (rows.length < 2) return { added: [], skipped: [{ row: 0, reason: "File appears empty or has no data rows" }] };

  const col = mapColumns(rows[0]);
  const missing = ["staff", "service", "date"].filter((f) => col[f] === undefined);
  if (col.client === undefined && col.email === undefined && col.phone === undefined) missing.unshift("client");
  if (missing.length) {
    return { added: [], skipped: [{ row: 0, reason: `Missing required column${missing.length > 1 ? "s" : ""}: ${missing.join(", ")}` }] };
  }

  const staffByName = new Map(staff.map((s) => [s.name.trim().toLowerCase(), s]));
  const staffByEmail = new Map(staff.filter((s) => s.email).map((s) => [s.email!.toLowerCase(), s]));
  const firstNameCounts = new Map<string, Staff[]>();
  for (const s of staff) {
    const first = s.name.trim().split(/\s+/)[0].toLowerCase();
    firstNameCounts.set(first, [...(firstNameCounts.get(first) ?? []), s]);
  }
  function findStaff(v: string): Staff | undefined {
    const k = v.trim().toLowerCase();
    return staffByName.get(k) ?? staffByEmail.get(k) ?? (firstNameCounts.get(k)?.length === 1 ? firstNameCounts.get(k)![0] : undefined);
  }
  const serviceByName = new Map(services.map((s) => [s.name.trim().toLowerCase(), s]));

  const added: ParsedAppointment[] = [];
  const skipped: { row: number; reason: string }[] = [];

  for (let i = 1; i < rows.length; i++) {
    const r = rows[i];
    const rowNum = i + 1;
    const g = (f: string) => (col[f] !== undefined ? (r[col[f]] ?? "").trim() : "");

    const clientName = g("client");
    const email = g("email").toLowerCase();
    const phone = g("phone");
    if (!clientName && !email && !phone) { skipped.push({ row: rowNum, reason: "Missing client" }); continue; }

    const staffRaw = g("staff");
    const st = staffRaw ? findStaff(staffRaw) : undefined;
    if (!st) { skipped.push({ row: rowNum, reason: staffRaw ? `Staff "${staffRaw}" not found` : "Missing staff" }); continue; }

    const serviceRaw = g("service");
    if (!serviceRaw) { skipped.push({ row: rowNum, reason: "Missing service" }); continue; }
    const svc = serviceByName.get(serviceRaw.toLowerCase());

    const d = parseDate(g("date"));
    if (!d) { skipped.push({ row: rowNum, reason: `Unrecognized date "${g("date")}" (use YYYY-MM-DD or MM/DD/YYYY)` }); continue; }
    const timeRaw = g("time") || d.rest;
    const t = timeRaw ? parseTime(timeRaw) : null;
    if (!t) { skipped.push({ row: rowNum, reason: timeRaw ? `Unrecognized time "${timeRaw}"` : "Missing time" }); continue; }
    const start = new Date(d.y, d.m - 1, d.d, t.h, t.min);
    if (isNaN(start.getTime()) || start.getMonth() !== d.m - 1) { skipped.push({ row: rowNum, reason: `Invalid date "${g("date")}"` }); continue; }

    const durRaw = g("duration");
    const duration = durRaw ? parseInt(durRaw, 10) : svc?.duration_min ?? 30;
    if (!duration || duration <= 0 || duration > 24 * 60) { skipped.push({ row: rowNum, reason: `Invalid duration "${durRaw}"` }); continue; }

    const priceRaw = g("price").replace(/[$,\s]/g, "");
    const price = priceRaw ? parseFloat(priceRaw) : svc?.price ?? 0;
    if (isNaN(price) || price < 0) { skipped.push({ row: rowNum, reason: `Invalid price "${g("price")}"` }); continue; }

    const status = parseStatus(g("status"), start);
    if (!status) { skipped.push({ row: rowNum, reason: `Unrecognized status "${g("status")}"` }); continue; }

    added.push({
      row: rowNum,
      client_name: clientName,
      client_email: email,
      client_phone: phone,
      staff_id: st.user_id,
      staff_name: st.name,
      service: svc?.name ?? serviceRaw,
      service_id: svc?.service_id,
      start,
      end: new Date(start.getTime() + duration * 60_000),
      price,
      status,
      customService: !svc,
    });
  }
  return { added, skipped };
}

// ── Modal ─────────────────────────────────────────────────────────────────────

const STATUS_TAG: Record<Status, { color: string; label: string }> = {
  scheduled: { color: "blue", label: "Scheduled" },
  completed: { color: "green", label: "Completed" },
  cancelled: { color: "default", label: "Cancelled" },
  no_show: { color: "red", label: "No-show" },
};

export default function ImportAppointmentsModal({
  staff,
  onClose,
  onImported,
}: {
  staff: Staff[];
  onClose: () => void;
  onImported: () => void;
}) {
  const [status, setStatus] = useState<ImportStatus>("idle");
  const [result, setResult] = useState<ParseResult | null>(null);
  const [response, setResponse] = useState<ImportResponse | null>(null);
  const [fileName, setFileName] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    axiosClient.get("/settings/services").then((r) => setServices(r.data ?? [])).catch(() => setServices([]));
  }, []);

  function processFile(file: File) {
    setFileName(file.name);
    setStatus("parsing");
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!ext || !["csv", "tsv", "txt"].includes(ext)) {
      const formatMessages: Record<string, string> = {
        xlsx: "Excel files aren't supported yet. In Excel: File → Save As → CSV, then import that file.",
        xls: "Excel files aren't supported yet. In Excel: File → Save As → CSV, then import that file.",
        ods: "OpenDocument spreadsheets aren't supported yet. Export as CSV instead.",
        pdf: "PDF files can't be imported. Export your appointments as CSV instead.",
      };
      const reason = (ext && formatMessages[ext]) || `Format .${ext} not supported. Please export as CSV.`;
      setStatus("error");
      setResult({ added: [], skipped: [{ row: 0, reason }] });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseAppointmentsCSV((e.target?.result as string) ?? "", staff, services);
      setResult(parsed);
      setStatus(parsed.added.length > 0 ? "preview" : "error");
    };
    reader.onerror = () => {
      setStatus("error");
      setResult({ added: [], skipped: [{ row: 0, reason: "Could not read the file" }] });
    };
    reader.readAsText(file);
  }

  async function doImport() {
    if (!result) return;
    setStatus("importing");
    try {
      const payload = result.added.map((a) => ({
        row: a.row,
        client_name: a.client_name,
        client_email: a.client_email,
        client_phone: a.client_phone,
        staff_id: a.staff_id,
        service: a.service,
        service_id: a.service_id,
        start_time: a.start.toISOString(),
        end_time: a.end.toISOString(),
        price: a.price,
        status: a.status,
      }));
      const resp = await axiosClient.post<ImportResponse>("/bookings/import", payload);
      setResponse(resp.data);
      setStatus("done");
      onImported();
    } catch (err: any) {
      setStatus("error");
      setResult({ added: [], skipped: [{ row: 0, reason: err?.response?.data || "Import failed. Please try again." }] });
    }
  }

  function reset() {
    setStatus("idle");
    setResult(null);
    setResponse(null);
    setFileName("");
  }

  const customServiceCount = result?.added.filter((a) => a.customService).length ?? 0;

  return (
    <Modal
      open
      title="Import Appointments"
      onCancel={onClose}
      maskClosable={status !== "importing"}
      closable={status !== "importing"}
      footer={
        (status === "preview" || status === "importing") && result ? (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button type="link" onClick={reset} disabled={status === "importing"}>← Choose different file</Button>
            <div style={{ display: "flex", gap: 8 }}>
              <Button onClick={onClose} disabled={status === "importing"}>Cancel</Button>
              <Button type="primary" loading={status === "importing"} onClick={doImport}>
                Import {result.added.length} Appointment{result.added.length !== 1 ? "s" : ""}
              </Button>
            </div>
          </div>
        ) : status === "done" ? (
          <Button type="primary" onClick={onClose}>Done</Button>
        ) : null
      }
      width={680}
    >
      {status === "done" && response && (
        <>
          <div className="import-done">
            <CheckCircle2 size={44} className="import-done-icon" />
            <div className="import-done-title">Import Complete</div>
            <div className="import-done-sub">
              {response.inserted} appointment{response.inserted !== 1 ? "s" : ""} added
              {response.clients_created > 0 && ` · ${response.clients_created} new client${response.clients_created !== 1 ? "s" : ""} created`}
              {response.duplicates > 0 && ` · ${response.duplicates} already existed (skipped)`}
            </div>
          </div>
          {response.failed.length > 0 && (
            <div className="import-preview-banner import-banner-warn">
              <AlertTriangle size={14} />
              <div>
                <strong>{response.failed.length} rows could not be imported</strong>
                <ul>
                  {response.failed.slice(0, 5).map((f, i) => <li key={i}>Row {f.row}: {f.reason}</li>)}
                  {response.failed.length > 5 && <li>…and {response.failed.length - 5} more</li>}
                </ul>
              </div>
            </div>
          )}
        </>
      )}

      {(status === "idle" || status === "parsing") && (
        <>
          <div
            className={`import-dropzone${dragOver ? " import-dropzone-over" : ""}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) processFile(f); }}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.tsv,.txt,.xlsx,.xls,.ods,.pdf"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); e.target.value = ""; }}
              style={{ display: "none" }}
            />
            {status === "parsing" ? (
              <div className="import-parsing">
                <div className="import-spinner" />
                <span>Parsing {fileName}…</span>
              </div>
            ) : (
              <>
                <Upload size={36} className="import-drop-icon" />
                <div className="import-drop-title">Drop CSV here or click to browse</div>
                <div className="import-drop-sub">Supports CSV, TSV, TXT</div>
              </>
            )}
          </div>
          <div className="import-hint">
            <FileText size={13} />
            <div>
              <div className="import-hint-label">Expected columns (flexible)</div>
              <code>client, email, phone, staff, service, date, time, duration, price, status</code>
              <div style={{ marginTop: 6 }}>
                Required: client (name, email or phone), staff, service, date and time. Staff are matched by name;
                services matching your service list use its duration and price unless the file gives them.
                New clients are created automatically. Rows with no status are marked completed if the date has
                passed, otherwise scheduled. Re-importing the same file won't create duplicates.
              </div>
            </div>
          </div>
        </>
      )}

      {(status === "preview" || status === "importing") && result && (
        <>
          <div className="import-preview-banner import-banner-success">
            <CheckCircle2 size={16} />
            <span>
              <strong>{result.added.length} appointments</strong> ready to import
              {result.skipped.length > 0 ? ` · ${result.skipped.length} rows skipped` : ""}
            </span>
          </div>
          <div className="import-table-wrap">
            <table className="import-table">
              <thead>
                <tr><th>Date &amp; Time</th><th>Client</th><th>Staff</th><th>Service</th><th>Status</th></tr>
              </thead>
              <tbody>
                {result.added.slice(0, 50).map((a) => (
                  <tr key={a.row}>
                    <td>{dayjs(a.start).format("MMM D, YYYY h:mm A")}</td>
                    <td>{a.client_name || a.client_email || a.client_phone}</td>
                    <td>{a.staff_name}</td>
                    <td>{a.service}{a.customService && <span style={{ color: "#9ca3af" }}> *</span>}</td>
                    <td><Tag color={STATUS_TAG[a.status].color}>{STATUS_TAG[a.status].label}</Tag></td>
                  </tr>
                ))}
                {result.added.length > 50 && (
                  <tr><td colSpan={5} className="more-row">…and {result.added.length - 50} more</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {customServiceCount > 0 && (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
              * {customServiceCount} appointment{customServiceCount !== 1 ? "s use services" : " uses a service"} not in your service list. They'll import with the name from the file.
            </div>
          )}
          {result.skipped.length > 0 && (
            <div className="import-preview-banner import-banner-warn">
              <AlertTriangle size={14} />
              <div>
                <strong>{result.skipped.length} rows skipped</strong>
                <ul>
                  {result.skipped.slice(0, 5).map((s, i) => (
                    <li key={i}>{s.row > 0 ? `Row ${s.row}: ` : ""}{s.reason}</li>
                  ))}
                  {result.skipped.length > 5 && <li>…and {result.skipped.length - 5} more</li>}
                </ul>
              </div>
            </div>
          )}
        </>
      )}

      {status === "error" && result && (
        <>
          <div className="import-preview-banner import-banner-error">
            <AlertTriangle size={16} />
            <div>
              <strong>Could not import file</strong>
              {result.skipped.slice(0, 8).map((s, i) => <div key={i}>{s.row > 0 ? `Row ${s.row}: ` : ""}{s.reason}</div>)}
              {result.skipped.length > 8 && <div>…and {result.skipped.length - 8} more</div>}
            </div>
          </div>
          <Button block onClick={reset}>Try a different file</Button>
        </>
      )}
    </Modal>
  );
}
